import styles from "./MasterUI.module.css";

import { SettingsWorkBox } from "../workBoxes/SettingsWorkBox";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { MasterTopBar } from "./MasterTopBar";
import { FullscreenContainer, FullscreenContainerRef, useOuterClickManyTargets } from "@kalyzee/kast-web-sdk/web-components";
import { OverlayContainer, ReactIcons, RevealContainer } from "@kalyzee/kalyzee-ui";
import { useAppStoreSelector } from "../../contexts/appStore/selectors";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";
import { useMeetingContainerContext } from "../../contexts/meetingContainer/context";
import { isExpert } from "../../helpers/roles";
import { toastInfo } from "../../helpers/toast";

export interface MasterUIProps {
  children: React.ReactNode;
  onBack?: () => void;
  backIcon?: "arrow" | "exit";
}

export const MasterUI = React.memo(({ children, onBack, backIcon }: MasterUIProps) => {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [meetingInfoOpen, setMeetingInfoOpen] = useState(false);
  // Mirror the container's REAL fullscreen state (not just our own toggles) so
  // the button shows "exit" whenever the app is already fullscreen — including
  // after a remount while fullscreen, or an exit via the Esc key.
  const [isFullscreen, setIsFullscreen] = useState(false);
  const fullscreenRef = useRef<FullscreenContainerRef>();
  const isMobile = useAppStoreSelector(state => state.isMobile);

  const settingsBtnRef: React.RefObject<HTMLDivElement> = useRef(null);
  const settingsBoxRef: React.RefObject<HTMLDivElement> = useRef(null);
  const meetingTimerRef: React.RefObject<HTMLDivElement> = useRef(null);
  const meetingInfoRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Meeting data for mobile info popup
  const containerCtx = useMeetingContainerContext({ raiseIfUndefined: false });
  const [meetingName] = useMeetingSelector(containerCtx?.containerAPI, "meeting", meeting => meeting?.name);
  const [watchingNumber] = useMeetingSelector(containerCtx?.containerAPI, "sessions", sessions => sessions?.filter(s => !isExpert(s)).length);

  useOuterClickManyTargets([settingsBtnRef, settingsBoxRef], () => {
    setSettingsOpen(false);
  });

  useOuterClickManyTargets([meetingTimerRef, meetingInfoRef], () => {
    setMeetingInfoOpen(false);
  });

  const onSettings = useCallback(() => {
    setSettingsOpen(v => !v);
  }, []);

  const onMeetingTimerClick = useCallback(() => {
    setMeetingInfoOpen(v => !v);
  }, []);

  // Reflect the REAL viewport fullscreen state — whatever put it there, not just
  // our own button. Two independent sources:
  //  - Fullscreen API (`document.fullscreenElement`): our container, or any
  //    other element that requested fullscreen. Tracked via `fullscreenchange`.
  //  - Browser fullscreen (F11): invisible to the Fullscreen API, but exposed by
  //    the `(display-mode: fullscreen)` media query.
  useEffect(() => {
    const mql = window.matchMedia("(display-mode: fullscreen)");
    const update = () => setIsFullscreen(!!document.fullscreenElement || mql.matches);
    update();
    document.addEventListener("fullscreenchange", update);
    mql.addEventListener("change", update);
    return () => {
      document.removeEventListener("fullscreenchange", update);
      mql.removeEventListener("change", update);
    };
  }, []);

  const onToggleFullscreen = useCallback(() => {
    // Exit whatever holds the Fullscreen API (our container or another element).
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
      return;
    }
    // Pure F11 fullscreen can't be exited programmatically (no fullscreenElement,
    // exitFullscreen is a no-op) — the browser only lets the user leave it. Tell
    // them, instead of a silent dead click; the icon already reflects the state.
    if (window.matchMedia("(display-mode: fullscreen)").matches) {
      toastInfo("Appuyez sur F11 pour revenir en fenêtré.");
      return;
    }
    fullscreenRef.current?.enter();
  }, []);

  const renderSettingsOverlayWorkBox = () => {
    if (isMobile) {
      return (
        <OverlayContainer
          visible={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          style={{ position: "absolute", inset: 0, zIndex: 10 }}
          overlay={
            <div
              ref={settingsBoxRef}
              className={styles.mobileSettingsOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <SettingsWorkBox containerRef={settingsBoxRef} onClose={() => setSettingsOpen(false)} />
            </div>
          }
        />
      );
    }

    return (
      <RevealContainer
        open={settingsOpen}
        duration={300}
        easing="ease-in-out"
        className={styles.settingsBox}
      >
        <SettingsWorkBox
          containerRef={settingsBoxRef}
          onClose={() => setSettingsOpen(false)}
        />
      </RevealContainer>
    );
  };
  
  const renderMeetingInfoPopup = () => {
    if (!isMobile || !meetingInfoOpen) return null;
    if (!meetingName && watchingNumber === undefined) return null;
    return (
      <div ref={meetingInfoRef} className={styles.meetingInfoPopup}>
        {meetingName && (
          <div className={styles.meetingInfoRow}>
            <span className={styles.meetingInfoLabel}>Cours</span>
            <span className={styles.meetingInfoValue}>{meetingName}</span>
          </div>
        )}
        {watchingNumber !== undefined && (
          <div className={styles.meetingInfoRow}>
            <span className={styles.meetingInfoLabel}>Participants</span>
            <span className={styles.meetingInfoValue}>
              <ReactIcons.Eye style={{ width: "1em", height: "1em" }} />
              {watchingNumber}
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <FullscreenContainer
      ref={fullscreenRef}
      useNative
      style={{ width: "100%", height: "100%" }}
    >
      <div className={styles.container}>
        <div style={{ position: "relative" }}>
          <MasterTopBar
            onSettings={onSettings}
            onBack={onBack}
            backIcon={backIcon}
            settingsBtnRef={settingsBtnRef}
            meetingTimerRef={meetingTimerRef}
            onMeetingTimerClick={isMobile ? onMeetingTimerClick : undefined}
            isFullscreen={isFullscreen}
            onToggleFullscreen={onToggleFullscreen}
          />
          {renderMeetingInfoPopup()}
        </div>
        <div className={styles.content}>
          {children}
          {renderSettingsOverlayWorkBox()}
        </div>
      </div>
    </FullscreenContainer>
  );
});